import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertCircle, Check, Calendar, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { SocialIcon } from '../icons/SocialIcon';
import { SocialProfilePicture } from '../common/SocialProfilePicture';
import { usePublishExistingPost } from '../../hooks/usePublishPost';
import { useToast } from '../../context/ToastContext';
import { MediaDisplay } from '../posts/previews/MediaDisplay';
import type { SocialPlatform } from '../icons/SocialIcon';
import { normalizePlatformName } from '../icons/SocialIcon';
import { getPost } from '../../services/postService';

// Extend the Brand interface to include social_accounts
interface SocialAccount {
  accountId: string;
  platform: string;
  pageId: string;
  username?: string;
  picture_url?: string;
  profile_url?: string;
}

interface Brand {
  id: string;
  name: string;
  logo?: string;
  social_accounts?: SocialAccount[];
}

// Define the platform result type
interface PlatformResult {
  success: boolean;
  data?: any;
  error?: any;
  message?: string;
  publish_id?: string;
  error_details?: {
    message: string;
    code?: number | string;
  };
  tiktok?: PlatformResult;
  linkedin?: PlatformResult;
  [key: string]: any;
}

// Define the publish result type
interface PublishResult {
  success: boolean;
  results?: {
    success?: boolean;
    results?: Record<string, PlatformResult>;
    tiktok?: PlatformResult;
    linkedin?: PlatformResult;
    [key: string]: any;
  };
  error?: string;
  message?: string;
  postId?: string;
  publish_id?: string;
}

// Success Animation Component
const SuccessAnimation: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const { t } = useTranslation();
  useEffect(() => {
    // Automatically trigger onComplete after animation finishes
    const timer = setTimeout(() => {
      onComplete();
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl p-12 flex flex-col items-center justify-center transform transition-all duration-500 animate-success-modal">
        <div className="relative mb-4">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-green-600 animate-success-check" />
          </div>
          <div className="absolute inset-0 rounded-full border-4 border-green-500 opacity-0 animate-success-ring"></div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 animate-success-text">{t('publishModal.successTitle', 'Success!')}</h2>
        <p className="text-gray-600 text-center animate-success-text">{t('publishModal.successMessage', 'Your post has been published successfully.')}</p>
      </div>
    </div>
  );
};

interface ConnectedAccount {
  platform: SocialPlatform;
  username: string;
  picture_url?: string;
}

interface PublishConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  postId: string; // ID of the existing draft post
  content: string;
  platforms: SocialPlatform[];
  brand?: Brand;
  hasMedia: boolean;
  mediaType?: 'image' | 'video';
  format: 'regular' | 'reel';
  scheduledAt?: string | null;
  media?: File | null;
  mediaUrl?: string | null;
  multiImages?: File[]; // New: support multi-image previews before save
  existingMediaUrls?: string[]; // New: support existing image URLs
  connectedAccounts?: ConnectedAccount[];
  autoStart?: boolean; // If true, start publish automatically when opened
}

// Define a type for individual platform publish status
type PlatformPublishStatus = 'idle' | 'publishing' | 'success' | 'error';

export const PublishConfirmationModal: React.FC<PublishConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  postId,
  content,
  platforms,
  brand,
  hasMedia,
  mediaType,
  format,
  scheduledAt,
  media,
  mediaUrl,
  multiImages = [],
  existingMediaUrls = [],
  connectedAccounts = [],
  autoStart = false
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const publishPostMutation = usePublishExistingPost();
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [detailedErrors, setDetailedErrors] = useState<Record<string, string>>({});
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [displayPlatforms, setDisplayPlatforms] = useState<SocialPlatform[]>(() => {
    const unique = Array.from(new Set(platforms.map(p => p)));
    return unique as SocialPlatform[];
  });
  const [platformStatuses, setPlatformStatuses] = useState<Record<SocialPlatform, PlatformPublishStatus>>(() => {
    const initialStatuses: Record<SocialPlatform, PlatformPublishStatus> = {} as Record<SocialPlatform, PlatformPublishStatus>;
    platforms.forEach(p => initialStatuses[p] = 'idle');
    return initialStatuses;
  });
  const [mediaLoadError, setMediaLoadError] = useState<{
    hasError: boolean;
    type: 'video' | 'image' | null;
    url: string;
  }>({
    hasError: false,
    type: null,
    url: ''
  });

  // Sync URL query with modal open state: add/remove ?modal=publishing
  useEffect(() => {
    const url = new URL(window.location.href);
    if (isOpen) {
      url.searchParams.set('modal', 'publishing');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    } else if (url.searchParams.get('modal') === 'publishing') {
      url.searchParams.delete('modal');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, [isOpen]);

  // Effect to reset statuses when platforms array changes or modal reopens
  useEffect(() => {
    const initialStatuses: Record<SocialPlatform, PlatformPublishStatus> = {} as Record<SocialPlatform, PlatformPublishStatus>;
    platforms.forEach(p => initialStatuses[p] = 'idle');
    setPlatformStatuses(initialStatuses);
  }, [platforms, isOpen]); // Depend on isOpen to reset when modal is opened anew

  // Ensure recap lists all platforms that will be published by merging local selection with backend post data
  useEffect(() => {
    const base = new Set<SocialPlatform>(platforms as SocialPlatform[]);
    setDisplayPlatforms(Array.from(base));

    const fetchAndMergePlatforms = async () => {
      try {
        if (!postId) return;
        const postData: any = await getPost(postId);
        if (!postData) return;
        let backendPlatforms: string[] = [];
        if (Array.isArray(postData.platforms)) {
          backendPlatforms = postData.platforms as string[];
        } else if (postData.platforms && typeof postData.platforms === 'object') {
          backendPlatforms = Object.keys(postData.platforms);
        }
        if (backendPlatforms.length > 0) {
          const normalized = backendPlatforms.map((p: string) => normalizePlatformName(p));
          const merged = new Set<SocialPlatform>([...Array.from(base), ...normalized]);
          setDisplayPlatforms(Array.from(merged));
        }
      } catch (e) {
        // Best-effort only; keep local selection if fetch fails
      }
    };
    void fetchAndMergePlatforms();
  }, [isOpen, postId, platforms]);

  // Auto-start publishing when requested
  useEffect(() => {
    if (isOpen && autoStart && !isPublishing) {
      // Defer to next tick to ensure modal renders first
      const timer = setTimeout(() => {
        void handlePublish();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoStart]);

  const getMediaPreview = (): { url: string, detectedType: 'image' | 'video' } => {
    // Enhanced debug logs to help identify the issue
    // console.log('Media debug:', { hasMedia, media, mediaUrl, mediaType, format });
    
    if (!hasMedia) return { url: '', detectedType: 'image' };
    
    let finalUrl = '';
    
    // Priority 0: if multiImages provided (e.g., LinkedIn carousel before save), preview the first file
    // Infer the type from the file itself to avoid treating videos as images
    if (Array.isArray(multiImages) && multiImages.length > 0) {
      const firstFile = multiImages[0];
      const blobUrl = URL.createObjectURL(firstFile);
      let inferredType: 'image' | 'video' = 'image';
      if (firstFile && typeof (firstFile as File).type === 'string') {
        if ((firstFile as File).type.startsWith('video/')) {
          inferredType = 'video';
        } else if ((firstFile as File).type.startsWith('image/')) {
          inferredType = 'image';
        }
      }
      // Fallback to extension-based inference when MIME type is generic or missing
      if (inferredType === 'image') {
        const fileName = ((firstFile as File).name || '').toLowerCase();
        if (fileName.endsWith('.mp4') || fileName.endsWith('.mov') || fileName.endsWith('.webm') || fileName.endsWith('.avi') || fileName.endsWith('.m4v')) {
          inferredType = 'video';
        }
      }
      // For reels, always preview as video
      const detectedType: 'image' | 'video' = (format === 'reel') ? 'video' : inferredType;
      return { url: blobUrl, detectedType };
    }

    // Priority 1: if we have a saved URL from backend, prefer it for preview (stable and shareable)
    if (typeof mediaUrl === 'string' && mediaUrl) {
      // console.log('Using mediaUrl as string:', mediaUrl);
      finalUrl = mediaUrl;
    }
    else if (mediaUrl && typeof mediaUrl === 'object') {
      const mediaObj = mediaUrl as any;
      if (mediaObj.url && typeof mediaObj.url === 'string') {
        // console.log('Using mediaUrl.url from object:', mediaObj.url);
        finalUrl = mediaObj.url;
      }
    }
    else if (media instanceof File) {
      // console.log('Creating blob URL from File object (fallback when no saved URL yet)');
      finalUrl = URL.createObjectURL(media);
    }
    else if (Array.isArray(existingMediaUrls) && existingMediaUrls.length > 0) {
      finalUrl = existingMediaUrls[0];
    }
    
    if (finalUrl && !finalUrl.startsWith('http') && !finalUrl.startsWith('blob:')) {
      if (finalUrl.startsWith('/')) {
        const baseUrl = window.location.origin;
        // console.log(`Converting relative URL to absolute: ${baseUrl}${finalUrl}`);
        finalUrl = `${baseUrl}${finalUrl}`;
      }
    }
    
    let finalDeterminedType: 'image' | 'video';

    // Priority 1: Explicit mediaType prop
    if (mediaType === 'video') {
      finalDeterminedType = 'video';
    } else if (mediaType === 'image') {
      finalDeterminedType = 'image';
    } else {
      // Priority 2: Infer from File object (if mediaType is undefined)
      let inferredFromFile: 'image' | 'video' | undefined;
      if (media instanceof File) {
        if (media.type.startsWith('video/')) {
          inferredFromFile = 'video';
        } else if (media.type.startsWith('image/')) {
          inferredFromFile = 'image';
        } else { // Check extension if type is generic
          const fileName = media.name.toLowerCase();
          if (fileName.endsWith('.mp4') || fileName.endsWith('.mov') || fileName.endsWith('.webm') || fileName.endsWith('.avi')) {
            inferredFromFile = 'video';
          } else if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png') || fileName.endsWith('.gif') || fileName.endsWith('.webp') || fileName.endsWith('.avif')) {
            inferredFromFile = 'image';
          }
        }
      }

      if (inferredFromFile) {
        finalDeterminedType = inferredFromFile;
      } else {
        // Priority 3: Infer from URL (if mediaType undefined and file inference failed/not applicable)
        let inferredFromUrl: 'image' | 'video' | undefined;
        if (finalUrl) {
          const lowerUrl = finalUrl.toLowerCase();
          if (lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.mov') || lowerUrl.endsWith('.avi') || lowerUrl.endsWith('.webm')) {
            inferredFromUrl = 'video';
          } else if (lowerUrl.endsWith('.jpg') || lowerUrl.endsWith('.jpeg') || lowerUrl.endsWith('.png') || lowerUrl.endsWith('.gif') || lowerUrl.endsWith('.webp') || lowerUrl.endsWith('.avif') || lowerUrl.endsWith('.svg')) {
            inferredFromUrl = 'image';
          } else if (lowerUrl.includes('video') && !lowerUrl.includes('video_game') && !lowerUrl.includes('gamingvideo')) { 
             inferredFromUrl = 'video';
          }
        }

        if (inferredFromUrl) {
          finalDeterminedType = inferredFromUrl;
        } else {
          // Priority 4: Fallback based on format (if mediaType is undefined and other inferences failed)
          finalDeterminedType = (format === 'reel' ? 'video' : 'image');
        }
      }
    }
    
    // Force reels to display as video in confirmation preview even if mediaType was incorrectly set
    if (format === 'reel') {
      finalDeterminedType = 'video';
    }

    // Final correction: if URL clearly indicates a video or image, override the type accordingly
    if (finalUrl) {
      const lowerUrl = finalUrl.toLowerCase();
      const looksLikeVideo = /(\.mp4|\.mov|\.avi|\.webm|\.m4v)(\?|#|$)/i.test(lowerUrl) || lowerUrl.includes('/video/') || lowerUrl.includes('video');
      const looksLikeImage = /(\.jpg|\.jpeg|\.png|\.gif|\.webp|\.avif|\.svg)(\?|#|$)/i.test(lowerUrl);
      if (looksLikeVideo) {
        finalDeterminedType = 'video';
      } else if (looksLikeImage) {
        finalDeterminedType = 'image';
      }
    }
    
    // Log the final URL we're using
    // console.log('Final media URL to be used:', finalUrl, 'with detected type:', finalDeterminedType);
    
    if (!finalUrl && hasMedia) {
      console.warn('No valid media source found despite hasMedia being true');
    }
    
    return { url: finalUrl, detectedType: finalDeterminedType };
  };

  const memoizedMediaPreviewData = useMemo(() => {
    return getMediaPreview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMedia, media, mediaUrl, mediaType, format]); // All dependencies getMediaPreview uses from component scope


  if (!isOpen) return null;

  const truncateContent = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  };

  const getPlatformUsername = (platform: SocialPlatform) => {
    // First try to find account in connectedAccounts
    const connectedAccount = connectedAccounts.find(acc => acc.platform.toLowerCase() === platform.toLowerCase());
    if (connectedAccount && connectedAccount.username) {
      return `@${connectedAccount.username}`;
    }
    
    if (!brand) return '';
    
    // Fallback to brand.social_accounts
    if (brand.social_accounts && Array.isArray(brand.social_accounts)) {
      // For Instagram, check both 'instagram' and 'facebook' platforms since Instagram can be connected via Facebook
      let account;
      if (platform.toLowerCase() === 'instagram') {
        // First try to find a direct Instagram account
        account = brand.social_accounts.find(acc => acc.platform.toLowerCase() === 'instagram');
        // If not found, try to find a Facebook account (Instagram connected via Facebook)
        if (!account) {
          account = brand.social_accounts.find(acc => acc.platform.toLowerCase() === 'facebook');
        }
      } else {
        account = brand.social_accounts.find(acc => acc.platform.toLowerCase() === platform.toLowerCase());
      }
      
      // If we found an account with a username, return it
      if (account && account.username) {
        return `@${account.username}`;
      }
    }
    
    // Fallback to the old behavior if no account found
    return `@${brand.name.toLowerCase().replace(/\s/g, '')}`;
  };

  const getMediaLabel = () => {
    if (!hasMedia) return t('publishModal.media.noMedia', 'No media');
    const type = memoizedMediaPreviewData.detectedType;
    const emoji = type === 'video' ? '🎥' : '🖼️';
    const typeText = t(`publishModal.media.type.${type}`, type);
    const formatText = t(`publishModal.media.format.${format === 'reel' ? 'reel' : 'attachment'}`, format);
    return `${emoji} ${typeText} ${formatText}`;
  };

  const showTikTokSuccessToast = () => {
    showToast(
      t('posts.publish.tiktokInitiated', 'Your post has been initiated on TikTok. We will show you on the Calendar page when your post is published.'),
      'success'
    );
  };

  const handleSuccessAndClose = () => {
    // Show success animation before closing
    setShowSuccessAnimation(true);
  };

  const handleAnimationComplete = () => {
    // After animation completes, close the modal and confirm
    setShowSuccessAnimation(false);
    onConfirm();
    onClose();
  };

  const handlePublish = async () => {
    try {
      // Set all platforms to 'publishing' state immediately
      setPlatformStatuses(prevStatuses => {
        const newStatuses = { ...prevStatuses };
        platforms.forEach(p => newStatuses[p] = 'publishing');
        return newStatuses;
      });

      setIsPublishing(true);
      setPublishError(null);
      setDetailedErrors({});

      // All validation should have been done before creating the draft
      if (!postId) {
        throw new Error('Missing post ID');
      }

      // If the post is scheduled, we don't need to do anything further
      if (scheduledAt) {
        console.log(`✅ Post scheduled successfully for: ${scheduledAt}`);
        handleSuccessAndClose();
        return;
      }

      console.log(`💬 Publishing draft post: ${postId}`);
      const publishResult = await publishPostMutation.mutateAsync(postId) as PublishResult;
      
      console.log('📊 Publish result:', JSON.stringify(publishResult, null, 2));

      // Check for early success conditions (LinkedIn, TikTok initiated, general initiated message)
      if (platforms.includes('linkedin')) {
        const linkedinResult = publishResult.results?.linkedin;
        if (linkedinResult?.success || (publishResult.message && publishResult.message.toLowerCase().includes('initiated'))) {
          console.log('LinkedIn publication initiated successfully');
          handleSuccessAndClose();
          return;
        }
      }
      if (platforms.includes('tiktok')) {
        const tiktokResultInResults = publishResult.results?.results?.tiktok;
        const tiktokDirectResult = publishResult.results?.tiktok;
        if ((tiktokResultInResults?.success) || (tiktokDirectResult?.success) || (tiktokResultInResults?.publish_id) || (tiktokDirectResult?.publish_id) || (publishResult.publish_id) || (publishResult.results?.publish_id)) {
          console.log('TikTok publication initiated successfully');
          handleSuccessAndClose();
          showTikTokSuccessToast();
          return;
        }
      }
      if (publishResult.message && (publishResult.message.toLowerCase().includes('initiated') || publishResult.message.toLowerCase().includes('initié'))) {
        console.log('Publication initiated successfully based on message');
        handleSuccessAndClose();
        return;
      }
      if (publishResult.success || (!publishResult.success && publishResult.results?.success)) {
        console.log('✅ Post published successfully (or specific results indicate success)!');
        handleSuccessAndClose();
        return;
      }

      // If we reach here, publication was not fully successful or requires detailed error checking.
      console.error('❌ Failed to publish draft post or partial failure:', publishResult);
      
      const newDetailedErrorsLocal: Record<string, string> = {};
      const newPlatformStatusesLocal: Record<SocialPlatform, PlatformPublishStatus> = { ...platformStatuses };
      let foundSpecificErrorLocal = false;
      let overallErrorMessageLocal = publishResult.error || publishResult.message || 'Failed to publish to some platforms'; 

      // Initialize all platforms to 'error' by default if we are in an error path, successful ones will be overridden
      platforms.forEach(p => {
        if (newPlatformStatusesLocal[p] !== 'success') { // Don't override if already marked success (e.g. TikTok)
           newPlatformStatusesLocal[p] = 'error';
        }
      });

      // Priority 1: publishResult.results is an array of platform outcomes
      if (publishResult.results && Array.isArray(publishResult.results)) {
        publishResult.results.forEach(item => {
          const platformResultItem = item as any; 
          if (platformResultItem && typeof platformResultItem === 'object' && platformResultItem.platform) {
            const platformNameKey = platformResultItem.platform as SocialPlatform;
            const platformNameDisplay = platformNameKey.charAt(0).toUpperCase() + platformNameKey.slice(1);
            
            if (platformResultItem.success) {
              newPlatformStatusesLocal[platformNameKey] = 'success';
            } else {
              newPlatformStatusesLocal[platformNameKey] = 'error';
              let specificErrorMessage = `Unknown error on ${platformNameDisplay}`;

              if (platformResultItem.response) {
                if (platformResultItem.response.error && typeof platformResultItem.response.error === 'string') {
                  specificErrorMessage = platformResultItem.response.error;
                } else if (platformResultItem.response.message && typeof platformResultItem.response.message === 'string' && platformResultItem.response.success === false) {
                  specificErrorMessage = platformResultItem.response.message;
                }
              } else if (platformResultItem.error && typeof platformResultItem.error === 'string') {
                specificErrorMessage = platformResultItem.error;
              } else if (platformResultItem.message && typeof platformResultItem.message === 'string') {
                specificErrorMessage = platformResultItem.message;
              }
              newDetailedErrorsLocal[platformNameDisplay] = specificErrorMessage;
              foundSpecificErrorLocal = true;
              console.log(`Detailed error for ${platformNameDisplay} (from array): ${specificErrorMessage}`);
            }
          }
        });
      }
      // Priority 2: publishResult.results.results is an object (platform keys)
      else if (publishResult.results?.results && typeof publishResult.results.results === 'object' && !Array.isArray(publishResult.results.results)) {
        for (const platformKey in publishResult.results.results) {
          const platformNameKey = platformKey as SocialPlatform;
          const platformResult = (publishResult.results.results as any)[platformKey] as PlatformResult;
          if (platformResult && typeof platformResult === 'object') {
            const platformNameDisplay = platformNameKey.charAt(0).toUpperCase() + platformNameKey.slice(1);
            if (platformResult.success) {
              newPlatformStatusesLocal[platformNameKey] = 'success';
            } else {
              newPlatformStatusesLocal[platformNameKey] = 'error';
              let specificErrorMessage = `Unknown error on ${platformNameDisplay}`;
              
              if (platformResult.error) {
                if (typeof platformResult.error === 'string') specificErrorMessage = platformResult.error;
                else if (typeof platformResult.error === 'object' && (platformResult.error as any).message) specificErrorMessage = (platformResult.error as any).message;
              } else if (platformResult.message && typeof platformResult.message === 'string') {
                specificErrorMessage = platformResult.message;
              }
              if (platformResult.data?.message && typeof platformResult.data.message === 'string' && platformResult.data.success === false) {
                  specificErrorMessage = platformResult.data.message;
              } else if (platformResult.error_details?.message && typeof platformResult.error_details.message === 'string') {
                  specificErrorMessage = platformResult.error_details.message;
              }
              newDetailedErrorsLocal[platformNameDisplay] = specificErrorMessage;
              foundSpecificErrorLocal = true;
              console.log(`Detailed error for ${platformNameDisplay} (from results.results): ${specificErrorMessage}`);
            }
          }
        }
      }
      // Priority 3: publishResult.results is an object (platform keys) - direct
      else if (publishResult.results && typeof publishResult.results === 'object' && !Array.isArray(publishResult.results)) {
        Object.keys(publishResult.results).forEach(key => {
          if (key === 'success' || key === 'results' || key ==='tiktok' || key === 'linkedin') { // Skip metadata/handled keys
              const value = (publishResult.results as any)[key];
              // check if value is an object, and if it is, if it has a success property of type boolean
              if (typeof value !== 'object' || value === null || typeof (value as PlatformResult).success !== 'boolean') return;
          }

          const platformNameKey = key as SocialPlatform;
          const platformResult = (publishResult.results as any)[key] as PlatformResult;

          if (platformResult && typeof platformResult === 'object' && typeof platformResult.success === 'boolean') { // Ensure it's a platform result
            const platformNameDisplay = platformNameKey.charAt(0).toUpperCase() + platformNameKey.slice(1);
            if (platformResult.success) {
              newPlatformStatusesLocal[platformNameKey] = 'success';
            } else {
              newPlatformStatusesLocal[platformNameKey] = 'error';
              let specificErrorMessage = `Unknown error on ${platformNameDisplay}`;
              
              if (platformResult.error) {
                if (typeof platformResult.error === 'string') specificErrorMessage = platformResult.error;
                else if (typeof platformResult.error === 'object' && (platformResult.error as any).message) specificErrorMessage = (platformResult.error as any).message;
              } else if (platformResult.message && typeof platformResult.message === 'string') {
                specificErrorMessage = platformResult.message;
              }
              if (platformResult.data?.message && typeof platformResult.data.message === 'string' && platformResult.data.success === false) {
                  specificErrorMessage = platformResult.data.message;
              } else if (platformResult.error_details?.message && typeof platformResult.error_details.message === 'string') {
                  specificErrorMessage = platformResult.error_details.message;
              }
              newDetailedErrorsLocal[platformNameDisplay] = specificErrorMessage;
              foundSpecificErrorLocal = true;
              console.log(`Detailed error for ${platformNameDisplay} (from results object): ${specificErrorMessage}`);
            }
          }
        });
      }
      
      // Handle cases where overall success is true but some platforms might have failed or vice-versa
      // This section ensures platformStatuses are aligned with the final outcome for each.
      platforms.forEach(p => {
        if (publishResult.success && newPlatformStatusesLocal[p] !== 'error') {
            // If overall is success and this platform isn't an error, mark as success.
            // This handles cases where platform-specific results might be missing but overall call succeeded.
            // Or, if a platform was initiated and considered success earlier (like TikTok special handling)
            if (newPlatformStatusesLocal[p] !== 'success'){
                 newPlatformStatusesLocal[p] = 'success';
            }
        } else if (!publishResult.success && newPlatformStatusesLocal[p] !== 'success') {
            // If overall is fail, and this platform isn't marked success (e.g. by specific TikTok rules),
            // ensure it is marked as 'error' if not already from detailed parsing.
            if (newPlatformStatusesLocal[p] === 'idle' || newPlatformStatusesLocal[p] === 'publishing'){
                 newPlatformStatusesLocal[p] = 'error'; 
            }
        }
      });


      // Special check for TikTok success despite overall error (if only TikTok was targeted)
      if (platforms.length === 1 && platforms.includes('tiktok')) {
        let tiktokSuccess = false;
        if (Array.isArray(publishResult.results)) {
            const tiktokItem = publishResult.results.find(item => (item as any).platform === 'tiktok');
            if (tiktokItem && (tiktokItem as any).success) tiktokSuccess = true;
        } else if (publishResult.results?.results?.tiktok?.success || publishResult.results?.tiktok?.success || publishResult.results?.results?.tiktok?.publish_id || publishResult.results?.tiktok?.publish_id) {
            tiktokSuccess = true;
        }
        
        if (tiktokSuccess) {
          console.log('⚠️ Interpreting as success for TikTok (single platform focus)');
          newPlatformStatusesLocal['tiktok'] = 'success'; 
          // If this was the only platform and it succeeded, trigger overall success flow
          if (!foundSpecificErrorLocal) { // Only if no OTHER errors were explicitly found for TikTok
              handleSuccessAndClose();
              showTikTokSuccessToast();
              return;
          }
        }
      }

      setPlatformStatuses(newPlatformStatusesLocal);
      
      if (foundSpecificErrorLocal && Object.keys(newDetailedErrorsLocal).length > 0) {
        setDetailedErrors(newDetailedErrorsLocal);
        const failedPlatforms = Object.keys(newDetailedErrorsLocal);
        if (failedPlatforms.length > 1) {
          overallErrorMessageLocal = `Failed to publish to ${failedPlatforms.length} platforms. See details below.`;
        } else { 
          overallErrorMessageLocal = `Failed to publish to ${failedPlatforms[0]}. See details below.`;
        }
      } else if (!publishResult.success && Object.keys(newDetailedErrorsLocal).length === 0) {
        // If overall success is false, but no specific platform errors were parsed, use the general error message.
        // This makes sure an error is displayed even if parsing fails to pinpoint it.
        // platforms.forEach(p => { // If no specific errors, mark all as error based on overall
        //   if (newPlatformStatusesLocal[p] !== 'success') newPlatformStatusesLocal[p] = 'error';
        // });
      } else if (publishResult.success && Object.keys(newDetailedErrorsLocal).length === 0) {
         // All platforms succeeded if overall is success and no detailed errors.
         platforms.forEach(p => newPlatformStatusesLocal[p] = 'success');
         setPlatformStatuses(newPlatformStatusesLocal);
         // This case should ideally be caught by earlier success returns, but as a fallback:
         console.log('✅ All platforms published successfully (fallback check)');
         handleSuccessAndClose();
         return;
      }

      setPublishError(overallErrorMessageLocal);

    } catch (error) {
      console.error('❌ Error publishing post:', error);
      setPublishError(error instanceof Error ? error.message : 'Failed to publish post');
      setDetailedErrors({}); // Clear detailed errors on a catch-all
    } finally {
      setIsPublishing(false);
    }
  };

  const getButtonText = () => {
    if (isPublishing) return t('publishModal.buttons.publishing', 'Publishing...');
    return scheduledAt ? t('publishModal.buttons.schedulePost', 'Schedule Post') : t('publishModal.buttons.publishNow', 'Publish Now');
  };

  return (
    <>
      {showSuccessAnimation && <SuccessAnimation onComplete={handleAnimationComplete} />}
      
      <div className="fixed inset-0 z-40 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
          
          <div className="relative bg-white rounded-xl border border-black shadow-2xl max-w-4xl w-full p-6 max-h-[80vh] overflow-y-auto transform transition-all duration-300 ease-in-out">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">{t('publishModal.title', 'Confirm Publication')}</h2>
              </div>
              <button
                onClick={onClose}
                disabled={isPublishing}
                className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Brand Info */}
              {brand && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <img 
                    src={brand.logo} 
                    alt={brand.name}
                    className="w-10 h-10 rounded-lg object-contain"
                  />
                  <div>
                    <h3 className="font-medium text-gray-900">{brand.name}</h3>
                    <p className="text-sm text-gray-500">{t('publishModal.brandPublishingAs', 'Publishing as brand')}</p>
                  </div>
                </div>
              )}

              {/* Schedule Info */}
              {scheduledAt && (
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="font-medium text-blue-900">{t('publishModal.schedule.title', 'Scheduled Publication')}</h3>
                    <p className="text-sm text-blue-700">
                      {t('publishModal.schedule.willPublishOn', 'Will be published on {{datetime}}', { datetime: new Date(scheduledAt).toLocaleString() })}
                    </p>
                  </div>
                </div>
              )}

              {/* Content Preview */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('publishModal.content.title', 'Content')}</h3>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex gap-4">
                    {/* Media Preview */}
                    {hasMedia && (
                      <div className={`relative flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden ${
                        format === 'reel' ? 'w-24 aspect-[9/16]' : 'w-24 aspect-square'
                      }`}>
                        {(() => {
                          const { url: mediaPreviewUrl, detectedType } = memoizedMediaPreviewData;
                          
                          if (!mediaPreviewUrl) {
                            return (
                              <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-500 text-xs p-2 text-center">
                                {t('publishModal.media.unavailable', 'Media preview unavailable')}
                              </div>
                            );
                          }
                          
                          return (
                            <>
                              <MediaDisplay
                                mediaUrl={mediaPreviewUrl}
                                altText="Preview"
                                type={detectedType}
                                objectFit="cover"
                                className="w-full h-full"
                                controls={detectedType === 'video'}
                                autoPlayOnMount={detectedType === 'video'}
                                muted
                                loop={detectedType === 'video'}
                              />
                              <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-black/50 text-white text-xs rounded">
                                {format === 'reel' ? '9:16' : '1:1'}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                    
                    {/* Content Text */}
                    <div className="flex-1">
                      <p className="text-gray-900 whitespace-pre-line break-words line-clamp-5">
                        {content}
                      </p>
                      <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                        <span>{getMediaLabel()}</span>
                        {format === 'reel' && (
                          <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs">
                            {t('publishModal.badge.reel', 'Reel format')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Platform List */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('publishModal.list.title', 'Will be published on')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {displayPlatforms.map((platform) => (
                    <div 
                      key={platform}
                      className="flex items-center justify-between p-3 border border-black rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {/* Find the specific account details for this platform */}
                        {(() => {
                          // First try to find account in connectedAccounts (which already works in PlatformsCard)
                          const connectedAccount = connectedAccounts.find(acc => acc.platform.toLowerCase() === platform.toLowerCase());
                          
                          if (connectedAccount) {
                            console.log(`[PublishConfirmationModal] Using connected account for ${platform}:`, connectedAccount);
                            return (
                              <SocialProfilePicture
                                platform={platform}
                                pictureUrl={connectedAccount.picture_url}
                                username={connectedAccount.username}
                                size="md"
                              />
                            );
                          }
                          
                          // Fallback to brand.social_accounts if connectedAccounts doesn't have this platform
                          let account;
                          if (platform.toLowerCase() === 'instagram') {
                            // First try to find a direct Instagram account
                            account = brand?.social_accounts?.find(acc => acc.platform.toLowerCase() === 'instagram');
                            // If not found, try to find a Facebook account (Instagram connected via Facebook)
                            if (!account) {
                              account = brand?.social_accounts?.find(acc => acc.platform.toLowerCase() === 'facebook');
                            }
                          } else {
                            account = brand?.social_accounts?.find(acc => acc.platform.toLowerCase() === platform.toLowerCase());
                          }
                          
                          // Log the account details for debugging
                          console.log(`[PublishConfirmationModal] Fallback account for ${platform}:`, account);
                          const pictureUrl = (account as any)?.profile_url || account?.picture_url || '';
                          console.log(`[PublishConfirmationModal] Picture URL for ${platform}:`, pictureUrl);
                          return (
                            <SocialProfilePicture
                              platform={platform}
                              pictureUrl={pictureUrl}
                              username={account?.username || ''}
                              size="md"
                            />
                          );
                        })()}
                        <div>
                          <p className="font-medium text-gray-900">
                            {platform.charAt(0).toUpperCase() + platform.slice(1)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {getPlatformUsername(platform)}
                          </p>
                        </div>
                      </div>
                      {platformStatuses[platform] === 'publishing' && (
                        <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
                      )}
                      {platformStatuses[platform] === 'success' && (
                        <Check className="w-5 h-5 text-green-500" />
                      )}
                      {platformStatuses[platform] === 'error' && (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      {platformStatuses[platform] === 'idle' && (
                        <Check className="w-5 h-5 text-gray-400" /> // Or a different idle icon, or nothing
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  {scheduledAt 
                    ? t('publishModal.warning.scheduled', 'This post will be published at the scheduled time.')
                    : t('publishModal.warning.publishAll', 'This action will publish your post to all selected platforms. This cannot be undone.')
                  }
                </p>
              </div>

              {publishError && (
                <div className="bg-red-50 border border-red-100 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-700">
                        {Object.keys(detailedErrors).length > 0 ? t('publishModal.errors.title', 'Publication Errors') : t('publishModal.errors.generic', 'Failed to publish post')}
                      </p>
                      <p className="text-sm text-red-600 mt-1 whitespace-pre-line">{publishError}</p>
                      {Object.keys(detailedErrors).length > 0 && (
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          {Object.entries(detailedErrors).map(([platform, errorMsg]) => (
                            <li key={platform} className="text-sm text-red-600">
                              <span className="font-semibold">{platform}:</span> {errorMsg}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={onClose}
                  disabled={isPublishing}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 disabled:opacity-50"
                >
                  {t('publishModal.buttons.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handlePublish}
                  disabled={isPublishing}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:bg-primary-400"
                >
                  {isPublishing ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {getButtonText()}
                    </span>
                  ) : getButtonText()}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublishConfirmationModal;